import { DrawerContextProvider, DrawerMode, useDrawer } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { TransactionDrawer } from '@views/transactions/routes/TransactionsList/elements/TransactionDrawer'

import { EmberRoute } from '../../../enums/emberRoute'
import { useEmberRouteToUrlGenerator } from '../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useEmberTransitionTo } from '../../../hooks/routing/useEmberTransitionTo'
import { useSegment } from '../../../hooks/useSegment'
import { EmberEventFn } from '../../../types/emberEventFn'
import { TransactionViewTrackingContext } from '../enums/transactionViewTrackingContext'
import { useFlowOption } from '../hooks/useFlowOption'
import { SelectedTransaction } from '../types/selectedTransaction'

interface TransactionViewContextValue {
  handleTransactionClick: (transaction: SelectedTransaction) => void
  isDrawerFlowEnabled: boolean
  isOpen: boolean
  selectedTransaction: SelectedTransaction | undefined
}

const TransactionViewContext = createContext<TransactionViewContextValue | undefined>(undefined)

interface TransactionViewProviderProps {
  children?: ReactNode
  transactionId?: string
  isDrawerOpen?: boolean
  onCloseEmber?: EmberEventFn
  /* Identifies the page this provider is mounted from, sent as `context` on the open-transaction tracking event */
  trackingContext: TransactionViewTrackingContext
  /* Special case for /accounts hash-routed view */
  /* Will be changed shortly with /accounts route reactification */
  isEmberHashRoutedView?: boolean
}

/* Two parter to use drawer context */
const TransactionViewProviderInner = ({
  children,
  transactionId,
  isDrawerOpen,
  onCloseEmber,
  trackingContext,
  isEmberHashRoutedView,
}: TransactionViewProviderProps): ReactElement => {
  const { isOpen, open } = useDrawer()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const { transitionTo } = useEmberTransitionTo()
  const navigate = useNavigate()
  const { track } = useSegment()
  const [selectedTransaction, setSelectedTransaction] = useState<SelectedTransaction | undefined>(undefined)

  const isBetaFlowEnabled = useFlowOption() === 'drawer'

  const openTransaction = useCallback(
    (transaction: SelectedTransaction) => {
      setSelectedTransaction(transaction)
      open()
    },
    [open],
  )

  const handleTransactionClick = useCallback(
    (transaction: SelectedTransaction) => {
      track('xxx - Bookkeeping - Ledger', {
        action: isBetaFlowEnabled ? 'Open drawer' : 'Open full view',
        context: trackingContext,
        transactionId: transaction.id,
      })

      if (isBetaFlowEnabled) {
        openTransaction(transaction)
        return
      }

      if (isEmberHashRoutedView) {
        transitionTo(EmberRoute.Transaction, transaction.id)
        onCloseEmber?.({ detail: null })
        return
      }

      if (!generateUrl) {
        return
      }

      navigate(generateUrl(EmberRoute.Transaction, transaction.id))
      onCloseEmber?.({ detail: null })
    },
    [
      isBetaFlowEnabled,
      openTransaction,
      isEmberHashRoutedView,
      transitionTo,
      generateUrl,
      navigate,
      onCloseEmber,
      track,
      trackingContext,
    ],
  )

  useEffect(() => {
    if (isDrawerOpen && transactionId) {
      handleTransactionClick({ id: transactionId })
    }
  }, [isDrawerOpen, transactionId, handleTransactionClick])

  const contextValue = useMemo(
    () => ({
      handleTransactionClick,
      isDrawerFlowEnabled: isBetaFlowEnabled,
      isOpen,
      selectedTransaction,
    }),
    [handleTransactionClick, isBetaFlowEnabled, isOpen, selectedTransaction],
  )

  return (
    <TransactionViewContext.Provider value={contextValue}>
      {children}
      <TransactionDrawer onSelectTransaction={setSelectedTransaction} transaction={selectedTransaction} />
    </TransactionViewContext.Provider>
  )
}

export const TransactionViewProvider = ({
  children,
  transactionId,
  isDrawerOpen,
  onCloseEmber,
  trackingContext,
  isEmberHashRoutedView,
}: TransactionViewProviderProps): ReactElement => {
  const handleDrawerModeChange = useCallback(
    (mode: DrawerMode) => {
      if (mode === DrawerMode.Closed) {
        onCloseEmber?.({ detail: null })
      }
    },
    [onCloseEmber],
  )

  return (
    <DrawerContextProvider onDrawerModeChange={onCloseEmber ? handleDrawerModeChange : undefined}>
      <TransactionViewProviderInner
        isDrawerOpen={isDrawerOpen}
        trackingContext={trackingContext}
        transactionId={transactionId}
        onCloseEmber={onCloseEmber}
        isEmberHashRoutedView={isEmberHashRoutedView}
      >
        {children}
      </TransactionViewProviderInner>
    </DrawerContextProvider>
  )
}

export const useTransactionView = (): TransactionViewContextValue => {
  const context = useContext(TransactionViewContext)

  if (!context) {
    throw new Error('TransactionViewProvider is missing in the module!')
  }

  return context
}
